import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import axios from "axios";
import {
  FileText, Upload, Bot, Folder, Book, User, Users,
  MessageSquare, Sparkles, BarChart, FileQuestion, Loader2, FileCheck,
  Link2, AlertTriangle, Package, FileUp, Briefcase, Search, X, Plus,
  UserCheck, CheckCircle, Shield, FileSpreadsheet, Video, Award,
  BarChart3, Trophy, FileEdit, Scale, GitCompare, Heart,
  ScanEye, FileSearch, BookOpen, Trash2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REPORT_TYPES = [
  // Original types
  { id: "profile", name: "Profile", icon: User, color: "bg-blue-500",
    description: "Generate a profile based on learned information (person or business details)",
    hoverTip: "Create detailed profiles from document intelligence" },
  { id: "detect_inaccuracies", name: "Detect Inaccuracies", icon: AlertTriangle, color: "bg-amber-500",
    description: "Detect inaccuracies and inconsistencies in loaded information",
    hoverTip: "Find contradictions and errors across your documents" },
  { id: "link_analysis", name: "Link Analysis", icon: Link2, color: "bg-purple-500",
    description: "Analyze links between entities (crime actors, financial flows, etc.)",
    hoverTip: "Map connections between people, organizations and data" },
  { id: "product", name: "Product", icon: Package, color: "bg-emerald-500",
    description: "Generate a formal product document with custom elements",
    hoverTip: "Create structured product documentation" },
  { id: "submission", name: "Submission", icon: FileUp, color: "bg-indigo-500",
    description: "Create a submission document for approval motivation",
    hoverTip: "Build approval-ready submission documents" },
  { id: "business_case", name: "Business Case", icon: Briefcase, color: "bg-rose-500",
    description: "Generate a comprehensive business case document",
    hoverTip: "Build ROI-driven business cases with executive summaries" },
  // New report types
  { id: "documents_summary", name: "Documents Summary", icon: FileSpreadsheet, color: "bg-cyan-500",
    description: "Provide a concise summary of key points, conclusions, and action items from attached documents",
    hoverTip: "Rapidly digest long reports, contracts, or research papers without reading them in full" },
  { id: "meeting_intelligence", name: "Meeting Intelligence", icon: Video, color: "bg-violet-500",
    description: "Extract decisions made, action items, and unresolved questions from meeting transcripts or recordings",
    hoverTip: "Ensure no follow-up task is missed and align everyone on outcomes without re-watching recordings" },
  { id: "executive_summary", name: "Executive Summary", icon: Award, color: "bg-yellow-500",
    description: "Synthesize multiple sources into a one-page strategic overview with top trends, risks, and opportunities",
    hoverTip: "Prepare leaders for decision-making by condensing weeks of research into a quick-read format" },
  { id: "data_narrative", name: "Data Narrative", icon: BarChart3, color: "bg-sky-600",
    description: "Translate your spreadsheet or data table into plain-language insights highlighting anomalies, trends, and correlations",
    hoverTip: "Turn complex datasets into a compelling story anyone can understand without needing a data analyst" },
  { id: "competitive_analysis", name: "Competitive Analysis", icon: Trophy, color: "bg-orange-500",
    description: "Compare our offerings, pricing, and positioning against specified competitors using publicly available data",
    hoverTip: "Compare content on documents — rapidly update the team on market moves" },
  { id: "proposal_generation", name: "Proposal Generation", icon: FileEdit, color: "bg-teal-500",
    description: "Generate a structured draft proposal including scope, timelines, and key value propositions based on rough input notes",
    hoverTip: "Cut the time to create client-facing documents from days to minutes" },
  { id: "policy_compliance", name: "Policy & Compliance Explainer", icon: Scale, color: "bg-blue-800",
    description: "Summarize a complex regulatory update or internal policy into plain language highlighting what team members must do",
    hoverTip: "Make compliance accessible and actionable, ensuring no one breaches rules due to misunderstanding" },
  { id: "process_discrepancy", name: "Process Discrepancy Finder", icon: GitCompare, color: "bg-red-500",
    description: "Compare documented standard operating procedures against a recorded screen-capture or workflow log to pinpoint variations",
    hoverTip: "Audit and refine internal processes to identify inefficiencies or training gaps instantly" },
  { id: "client_sentiment", name: "Client Sentiment & Feedback Synthesis", icon: Heart, color: "bg-pink-500",
    description: "Aggregate and analyze client communications and feedback to identify prevailing sentiment, churn risks, and testimonials",
    hoverTip: "Gain a real-time view of customer health without manually reviewing every email and survey response" }
];

export default function SmartFiles() {
  const auth = useAuth();
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("files");
  const fileInputRef = useRef(null);

  // Report Generation State
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [reportForm, setReportForm] = useState({
    analysisType: "crime_actors",
    analysisSubject: "",
    productType: "",
    title: "",
    mainBody: "",
    compilerDetails: "",
    submissionPurpose: "",
    motivation: "",
    businessObjective: "",
    proposedSolution: "",
    additionalNotes: ""
  });

  // Approval Workflow State
  const [qualityController, setQualityController] = useState(null);
  const [recommenders, setRecommenders] = useState([]);
  const [approver, setApprover] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchingFor, setSearchingFor] = useState(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [lastReport, setLastReport] = useState(null);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/smart-office/files/list`, {
        headers: getHeaders(),
        withCredentials: true
      });
      const data = res.data || {};
      const allFiles = [...(data.files || []), ...(data.folders || [])];
      setFiles(allFiles.filter(f => f.type === "file"));
    } catch (err) {
      toast.error("Failed to load files");
    }
    setIsLoading(false);
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setIsLoading(true);
    try {
      await axios.post(`${API}/smart-office/files/upload`, formData, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
        withCredentials: true
      });
      toast.success("File uploaded");
      fetchFiles();
    } catch (err) {
      toast.error("Upload failed");
    }
    setIsLoading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const learnFile = async (fileId) => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/smart-office/files/${fileId}/learn`, {}, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success("File learned! Reports Generator can now analyze it.");
      fetchFiles();
    } catch (err) {
      toast.error("Failed to learn file");
    }
    setIsLoading(false);
  };

  const deleteFile = async (fileId) => {
    try {
      await axios.delete(`${API}/smart-office/files/${fileId}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success("File deleted");
      setSelectedFileIds(prev => prev.filter(id => id !== fileId));
      fetchFiles();
    } catch (err) {
      toast.error("Failed to delete file");
    }
    setShowDeleteConfirm(false);
    setFileToDelete(null);
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFileIds(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const selectAllLearned = () => {
    const learnedIds = files.filter(f => f.learned).map(f => f.id);
    setSelectedFileIds(learnedIds);
  };

  const clearSelection = () => {
    setSelectedFileIds([]);
  };


  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/comms/chat/users/search?q=${encodeURIComponent(query)}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setSearchResults(res.data || []);
    } catch (err) {
      console.error("Search failed");
    }
  };

  const selectUser = (user) => {
    if (searchingFor === "qc") {
      setQualityController(user);
    } else if (searchingFor === "recommender") {
      if (!recommenders.find(r => r.user_id === user.user_id)) {
        setRecommenders([...recommenders, user]);
      }
    } else if (searchingFor === "approver") {
      setApprover(user);
    }
    setSearchQuery("");
    setSearchResults([]);
    setSearchingFor(null);
  };

  const removeRecommender = (userId) => {
    setRecommenders(recommenders.filter(r => r.user_id !== userId));
  };

  const openReportDialog = (reportType) => {
    setSelectedReportType(reportType);
    setReportForm({
      analysisType: "crime_actors",
      analysisSubject: "",
      productType: "",
      title: "",
      mainBody: "",
      compilerDetails: "",
      submissionPurpose: "",
      motivation: "",
      businessObjective: "",
      proposedSolution: "",
      additionalNotes: ""
    });
    setQualityController(null);
    setRecommenders([]);
    setApprover(null);
    setShowReportDialog(true);
  };

  const generateReport = async () => {
    if (!selectedReportType) return;

    setIsGeneratingReport(true);
    try {
      const reportData = {
        report_type: selectedReportType.id,
        ...reportForm,
        file_ids: selectedFileIds,
        workflow: {
          quality_controller: qualityController ? { user_id: qualityController.user_id, name: qualityController.name } : null,
          recommenders: recommenders.map(r => ({ user_id: r.user_id, name: r.name })),
          approver: approver ? { user_id: approver.user_id, name: approver.name } : null
        },
        generated_at: new Date().toISOString()
      };

      const res = await axios.post(`${API}/smart-office/files/report`, reportData, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });

      setLastReport({
        type: selectedReportType.name,
        content: res.data.content,
        time: new Date().toLocaleString()
      });

      setShowReportDialog(false);
      setActiveTab("chat");
      toast.success(`${selectedReportType.name} report generated!`);
    } catch (err) {
      console.error("Report gen error:", err.response?.status, err.response?.data);
      const msg = err.response?.data?.detail || err.message || "Failed to generate report";
      toast.error(msg);
    }
    setIsGeneratingReport(false);
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const learnedFiles = files.filter(f => f.learned);

  const renderReportForm = () => {
    if (!selectedReportType) return null;

    switch (selectedReportType.id) {
      case "profile":
        return (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Reports Generator will analyze all learned documents and generate a comprehensive profile
              based on information found about persons or businesses.
            </p>
            <div>
              <Label className="text-xs">Additional Focus (Optional)</Label>
              <Input
                value={reportForm.additionalNotes}
                onChange={(e) => setReportForm({...reportForm, additionalNotes: e.target.value})}
                placeholder="e.g., Focus on financial history, relationships..."
                className="mt-1"
              />
            </div>
          </div>
        );

      case "detect_inaccuracies":
        return (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Reports Generator will scan all learned documents to detect inconsistencies,
              contradictions, and potential inaccuracies in the information.
            </p>
            <div>
              <Label className="text-xs">Specific Areas to Check (Optional)</Label>
              <Textarea
                value={reportForm.additionalNotes}
                onChange={(e) => setReportForm({...reportForm, additionalNotes: e.target.value})}
                placeholder="e.g., Check dates, financial figures, names..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
        );

      case "link_analysis":
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Analysis Type</Label>
              <Select value={reportForm.analysisType} onValueChange={(v) => setReportForm({...reportForm, analysisType: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="crime_actors">Crime Actors</SelectItem>
                  <SelectItem value="financial_flows">Financial Flows</SelectItem>
                  <SelectItem value="organizational">Organizational Links</SelectItem>
                  <SelectItem value="communication">Communication Patterns</SelectItem>
                  <SelectItem value="geographic">Geographic Connections</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Subject/Entity to Analyze</Label>
              <Input
                value={reportForm.analysisSubject}
                onChange={(e) => setReportForm({...reportForm, analysisSubject: e.target.value})}
                placeholder="Enter name, entity, or keyword to focus on..."
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Additional Parameters (Optional)</Label>
              <Textarea
                value={reportForm.additionalNotes}
                onChange={(e) => setReportForm({...reportForm, additionalNotes: e.target.value})}
                placeholder="e.g., Time period, depth of analysis, specific connections..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
        );

      case "product":
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Product Type *</Label>
                <Input
                  value={reportForm.productType}
                  onChange={(e) => setReportForm({...reportForm, productType: e.target.value})}
                  placeholder="e.g., Intelligence Report, Analysis..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <Input
                  type="date"
                  value={new Date().toISOString().split('T')[0]}
                  disabled
                  className="mt-1 bg-app"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Title *</Label>
              <Input
                value={reportForm.title}
                onChange={(e) => setReportForm({...reportForm, title: e.target.value})}
                placeholder="Enter product title..."
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Main Body / Instructions *</Label>
              <Textarea
                value={reportForm.mainBody}
                onChange={(e) => setReportForm({...reportForm, mainBody: e.target.value})}
                placeholder="Describe what the product should contain, key points to address..."
                className="mt-1"
                rows={4}
              />
            </div>
            <div>
              <Label className="text-xs">Compiler Details</Label>
              <Input
                value={reportForm.compilerDetails}
                onChange={(e) => setReportForm({...reportForm, compilerDetails: e.target.value})}
                placeholder="Compiler name, department, contact..."
                className="mt-1"
              />
            </div>
          </div>
        );

      case "submission":
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Submission Purpose *</Label>
              <Input
                value={reportForm.submissionPurpose}
                onChange={(e) => setReportForm({...reportForm, submissionPurpose: e.target.value})}
                placeholder="What are you requesting approval for?"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Title *</Label>
              <Input
                value={reportForm.title}
                onChange={(e) => setReportForm({...reportForm, title: e.target.value})}
                placeholder="Submission title..."
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Motivation / Justification *</Label>
              <Textarea
                value={reportForm.motivation}
                onChange={(e) => setReportForm({...reportForm, motivation: e.target.value})}
                placeholder="Provide detailed motivation for this submission..."
                className="mt-1"
                rows={4}
              />
            </div>
            <div>
              <Label className="text-xs">Supporting Information (Optional)</Label>
              <Textarea
                value={reportForm.additionalNotes}
                onChange={(e) => setReportForm({...reportForm, additionalNotes: e.target.value})}
                placeholder="Any additional supporting details..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
        );

      case "business_case":
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title *</Label>
              <Input
                value={reportForm.title}
                onChange={(e) => setReportForm({...reportForm, title: e.target.value})}
                placeholder="Business case title..."
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Business Objective *</Label>
              <Textarea
                value={reportForm.businessObjective}
                onChange={(e) => setReportForm({...reportForm, businessObjective: e.target.value})}
                placeholder="What business problem are you solving? What is the goal?"
                className="mt-1"
                rows={3}
              />
            </div>
            <div>
              <Label className="text-xs">Proposed Solution *</Label>
              <Textarea
                value={reportForm.proposedSolution}
                onChange={(e) => setReportForm({...reportForm, proposedSolution: e.target.value})}
                placeholder="Describe the proposed solution or approach..."
                className="mt-1"
                rows={3}
              />
            </div>
            <div>
              <Label className="text-xs">Additional Context (Optional)</Label>
              <Textarea
                value={reportForm.additionalNotes}
                onChange={(e) => setReportForm({...reportForm, additionalNotes: e.target.value})}
                placeholder="Budget considerations, timeline, risks..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">{selectedReportType?.description}</p>
            <div>
              <Label className="text-xs">Additional Context (Optional)</Label>
              <Textarea
                value={reportForm.additionalNotes}
                onChange={(e) => setReportForm({...reportForm, additionalNotes: e.target.value})}
                placeholder="Add any specific context or instructions for this report..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800">REPORTS GENERATOR</h1>
            <p className="text-slate-500">AI-powered document intelligence, report generation and analysis</p>
          </div>
          <div className="flex items-center gap-1 text-sm text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full">
            <Book className="w-4 h-4" />
            <span>{learnedFiles.length} files learned</span>
          </div>
        </div>

        <div className="flex gap-2 mb-6">
          {[
            { id: "files", label: "Files", icon: Folder },
            { id: "chat", label: "AI Chat", icon: MessageSquare },
            { id: "reports", label: "Reports", icon: FileText }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "outline"}
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id ? "bg-violet-500 hover:bg-violet-600" : ""}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Files Panel */}
          <div className={`lg:col-span-${activeTab === "files" ? "3" : "1"} bg-white rounded-xl shadow-sm border p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-slate-800">Your Files</h2>
              <div>
                <input type="file" ref={fileInputRef} onChange={uploadFile} className="hidden" accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx,.xls,.json" />
                <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={isLoading} data-testid="upload-file-btn">
                  <Upload className="w-4 h-4 mr-1" /> Upload
                </Button>
              </div>
            </div>

            {files.length > 0 && (
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={selectAllLearned} className="text-xs h-7 px-2">
                    Select All Learned
                  </Button>
                  <Button size="sm" variant="ghost" onClick={clearSelection} className="text-xs h-7 px-2">
                    Clear
                  </Button>
                </div>
                {selectedFileIds.length > 0 && (
                  <span className="text-xs text-violet-600 font-medium">
                    {selectedFileIds.length} selected
                  </span>
                )}
              </div>
            )}
            <ScrollArea className={activeTab === "files" ? "h-[55vh]" : "h-56"}>
              <div className="space-y-2">
                {files.map(file => (
                  <div key={file.id} className={`p-3 rounded-lg border ${file.learned ? "bg-emerald-50 border-emerald-200" : "bg-app"}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {file.learned && (
                          <input
                            type="checkbox"
                            checked={selectedFileIds.includes(file.id)}
                            onChange={() => toggleFileSelection(file.id)}
                            className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                          />
                        )}
                        <FileText className={`w-5 h-5 ${file.learned ? "text-emerald-600" : "text-slate-400"}`} />
                        <div>
                          <p className="font-medium text-sm text-slate-800 truncate max-w-[180px]">{file.filename}</p>
                          <p className="text-xs text-slate-500">{file.learned ? "✓ Learned" : "Not learned"}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {!file.learned && (
                          <Button size="sm" variant="ghost" onClick={() => learnFile(file.id)} title="Learn this file" data-testid={`learn-file-${file.id}`}>
                            <Sparkles className="w-4 h-4 text-violet-500" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setFileToDelete(file); setShowDeleteConfirm(true); }}
                          title="Delete file"
                          className="text-red-400 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {files.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <Folder className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No files uploaded yet</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Chat/Reports Panel */}
          {(activeTab === "chat" || activeTab === "reports") && (
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border flex flex-col" style={{ height: "70vh" }}>
              <div className="p-4 border-b bg-gradient-to-r from-violet-500 to-purple-600">
                <h2 className="font-semibold text-white flex items-center gap-2">
                  <Bot className="w-5 h-5" /> Reports Generator
                </h2>
                <p className="text-xs text-violet-100">Intelligent document analysis and report generation</p>
              </div>

              {activeTab === "reports" && (
                <div className="p-4 border-b bg-app">
                  <p className="text-sm font-medium text-slate-700 mb-3">Select Report Type:</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {REPORT_TYPES.map(rt => (
                      <button
                        key={rt.id}
                        onClick={() => openReportDialog(rt)}
                        className="flex flex-col items-center p-3 rounded-lg border bg-white hover:border-violet-300 hover:shadow-sm transition-all text-center relative group"
                        data-testid={`report-type-${rt.id}`}
                        title={rt.hoverTip}
                      >
                        <div className={`w-10 h-10 rounded-full ${rt.color} flex items-center justify-center mb-2`}>
                          <rt.icon className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-xs font-medium text-slate-700">{rt.name}</span>
                        {/* Tooltip on hover */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 shadow-lg">
                          {rt.hoverTip}
                          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                        </div>
                      </button>
                    ))}
                  </div>
                  {learnedFiles.length === 0 && (
                    <p className="text-xs text-amber-600 mt-3 text-center">Learn at least one file to generate reports</p>
                  )}
                </div>
              )}

              <div className="flex-1 overflow-hidden">
                {lastReport && (
                  <div className="bg-violet-50 border-b border-violet-200 p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className="text-xs font-semibold text-violet-700 uppercase">{lastReport.type} REPORT</span>
                        <span className="text-xs text-violet-400 ml-2">{lastReport.time}</span>
                      </div>
                      <button onClick={() => setLastReport(null)} className="text-violet-400 hover:text-violet-600 text-xs">X</button>
                    </div>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap max-h-60 overflow-y-auto">
                      {lastReport.content}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Report Generation Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedReportType && (
                <>
                  <div className={`w-8 h-8 rounded-full ${selectedReportType.color} flex items-center justify-center`}>
                    <selectedReportType.icon className="w-4 h-4 text-white" />
                  </div>
                  <span>{selectedReportType.name} Report</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {selectedReportType && (
              <p className="text-sm text-slate-600 bg-app p-3 rounded-lg">
                {selectedReportType.description}
              </p>
            )}

            {/* Report-specific form */}
            {renderReportForm()}

            {/* Selected Files Info */}
            {selectedFileIds.length > 0 && (
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                <p className="text-xs text-violet-700 font-medium">
                  {selectedFileIds.length} file{selectedFileIds.length > 1 ? 's' : ''} selected for report generation
                </p>
                <p className="text-xs text-violet-500 mt-0.5 truncate">
                  {files.filter(f => selectedFileIds.includes(f.id)).map(f => f.filename).join(', ')}
                </p>
              </div>
            )}

            {/* Approval Workflow Section */}
            <div className="border-t pt-4 mt-4">
              <h3 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Approval Workflow (Optional)
              </h3>

              {/* Quality Controller */}
              <div className="mb-3">
                <Label className="text-xs flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Quality Controller
                </Label>
                {qualityController ? (
                  <div className="flex items-center justify-between mt-1 p-2 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2">
                      <Avatar className="w-6 h-6">
                        <AvatarFallback className="text-xs bg-blue-200 text-blue-700">{getInitials(qualityController.name)}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{qualityController.name}</span>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => setQualityController(null)} className="h-6 w-6 p-0 text-red-500">
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="relative mt-1">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={searchingFor === "qc" ? searchQuery : ""}
                      onChange={(e) => { setSearchQuery(e.target.value); searchUsers(e.target.value); }}
                      onFocus={() => setSearchingFor("qc")}
                      placeholder="Search for QC..."
                      className="pl-8 h-8"
                    />
                    {searchingFor === "qc" && searchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-32 overflow-auto">
                        {searchResults.map((user) => (
                          <div key={user.user_id} onClick={() => selectUser(user)} className="flex items-center gap-2 p-2 hover:bg-app cursor-pointer">
                            <Avatar className="w-6 h-6"><AvatarFallback className="text-xs">{getInitials(user.name)}</AvatarFallback></Avatar>
                            <span className="text-sm">{user.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Recommenders */}
              <div className="mb-3">
                <Label className="text-xs flex items-center gap-1">
                  <UserCheck className="w-3 h-3" /> Recommenders
                </Label>
                <div className="flex flex-wrap gap-1.5 mt-1 mb-2">
                  {recommenders.map((r, idx) => (
                    <div key={r.user_id} className="flex items-center gap-1.5 bg-purple-50 border border-purple-200 rounded-full px-2 py-1">
                      <span className="text-xs text-purple-600 font-medium">R{idx + 1}:</span>
                      <span className="text-xs">{r.name}</span>
                      <button onClick={() => removeRecommender(r.user_id)} className="text-red-400 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={searchingFor === "recommender" ? searchQuery : ""}
                    onChange={(e) => { setSearchQuery(e.target.value); searchUsers(e.target.value); }}
                    onFocus={() => setSearchingFor("recommender")}
                    placeholder={`Add Recommender ${recommenders.length + 1}...`}
                    className="pl-8 h-8"
                  />
                  {searchingFor === "recommender" && searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-32 overflow-auto">
                      {searchResults.map((user) => (
                        <div key={user.user_id} onClick={() => selectUser(user)} className="flex items-center gap-2 p-2 hover:bg-app cursor-pointer">
                          <Avatar className="w-6 h-6"><AvatarFallback className="text-xs">{getInitials(user.name)}</AvatarFallback></Avatar>
                          <span className="text-sm">{user.name}</span>
                          <Plus className="w-3 h-3 ml-auto text-purple-500" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Approver */}
              <div className="mb-3">
                <Label className="text-xs flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Approver
                </Label>
                {approver ? (
                  <div className="flex items-center justify-between mt-1 p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center gap-2">
                      <Avatar className="w-6 h-6">
                        <AvatarFallback className="text-xs bg-emerald-200 text-emerald-700">{getInitials(approver.name)}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{approver.name}</span>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => setApprover(null)} className="h-6 w-6 p-0 text-red-500">
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="relative mt-1">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={searchingFor === "approver" ? searchQuery : ""}
                      onChange={(e) => { setSearchQuery(e.target.value); searchUsers(e.target.value); }}
                      onFocus={() => setSearchingFor("approver")}
                      placeholder="Search for Approver..."
                      className="pl-8 h-8"
                    />
                    {searchingFor === "approver" && searchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-32 overflow-auto">
                        {searchResults.map((user) => (
                          <div key={user.user_id} onClick={() => selectUser(user)} className="flex items-center gap-2 p-2 hover:bg-app cursor-pointer">
                            <Avatar className="w-6 h-6"><AvatarFallback className="text-xs">{getInitials(user.name)}</AvatarFallback></Avatar>
                            <span className="text-sm">{user.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Generate Button */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowReportDialog(false)}>Cancel</Button>
              <Button
                onClick={generateReport}
                disabled={isGeneratingReport || selectedFileIds.length === 0}
                className="bg-violet-600 hover:bg-violet-500"
                data-testid="generate-report-btn"
              >
                {isGeneratingReport ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Generate Report</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete File
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Are you sure you want to delete <strong>{fileToDelete?.filename}</strong>? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => fileToDelete && deleteFile(fileToDelete.id)}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
